import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Star,
  Navigation,
  Phone,
  Globe,
  ExternalLink,
  ChevronRight,
  Car,
  Bath,
  Waves,
  Accessibility,
  PawPrint,
  Share2,
  Clock,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import BeachMap from "@/components/BeachMap";
import BeachCard from "@/components/BeachCard";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getBeachBySlug, getNearbyBeaches } from "@/lib/api";
import { toast } from "sonner";

// Deterministic hash from string for consistent "random" selection per beach
const hashStr = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const pick = (arr, hash) => arr[hash % arr.length];

const OurTake = ({ beach, activeAmenities }) => {
  const h = hashStr(beach.slug || beach.name);
  const city = beach.city;
  const state = beach.state;
  const name = beach.name;
  const hasParking = beach.has_parking;
  const hasBathrooms = beach.has_bathrooms;
  const hasShowers = beach.has_showers;
  const isAccessible = beach.is_accessible;
  const isPetFriendly = beach.is_pet_friendly;
  const rating = beach.average_rating;
  const reviews = beach.total_reviews;
  const subTypes = beach.sub_types || [];

  // Build unique paragraphs based on real data
  const openers = [
    `${name} is one of those spots in ${city} that locals tend to keep to themselves.`,
    `If you're heading to ${city}, ${state}, ${name} is a must on your list.`,
    `There's something special about ${name} that sets it apart from the typical beach access in ${city}.`,
    `${name} sits in the heart of ${city}'s coastline and offers a genuine ${state} beach experience.`,
    `Looking for a solid beach day in ${city}? ${name} delivers.`,
    `Among the public access points in ${city}, ${name} stands out for good reason.`,
    `${name} is the kind of beach access you stumble upon and wonder why you hadn't visited sooner.`,
    `For visitors exploring ${state}'s coastline, ${name} in ${city} is a worthy stop.`,
  ];

  const areaVibes = [
    `The ${city} area brings a relaxed, welcoming vibe that makes it easy to spend a full day out here.`,
    `${city}, ${state} has a character all its own — and this beach access captures that energy perfectly.`,
    `The surrounding area of ${city} is a mix of local charm and coastal beauty that feels authentic.`,
    `${city} isn't as overrun as some of the bigger beach towns in ${state}, which works in your favor here.`,
    `What we like about ${city} is that it still feels like a real community, not just a tourist destination.`,
    `The neighborhood around ${name} is quintessential ${state} — expect a friendly, laid-back atmosphere.`,
  ];

  // Amenity-specific commentary
  let amenityLines = [];
  if (hasParking) {
    amenityLines.push(pick([
      "Having dedicated parking here is a real plus — beach access with easy parking is rarer than you'd think.",
      "The parking situation is a standout feature. No circling blocks or hunting for meters.",
      "We appreciate that parking is available on-site. It makes planning a visit significantly easier.",
    ], h + 1));
  }
  if (hasBathrooms) {
    amenityLines.push(pick([
      "Restroom facilities are available, which is a practical convenience that many access points lack.",
      "The on-site restrooms are a welcome touch — especially if you're bringing the family.",
    ], h + 2));
  }
  if (hasShowers) {
    amenityLines.push(pick([
      "Outdoor showers are a nice detail — rinse off the sand before heading to your next stop.",
      "The shower facilities make this a notch above most basic beach access points.",
    ], h + 3));
  }
  if (isAccessible) {
    amenityLines.push(pick([
      "We're glad to see wheelchair accessibility here — it's an important feature that not every beach offers.",
      "Accessibility features make this a more inclusive option for visitors of all abilities.",
    ], h + 4));
  }
  if (isPetFriendly) {
    amenityLines.push(pick([
      "Dog owners, take note — this is one of the pet-friendly spots in the area.",
      "If you're traveling with your four-legged friend, you'll be happy to know pets are welcome here.",
    ], h + 5));
  }

  // Rating-based commentary
  let ratingLine = "";
  if (rating && rating >= 4.5) {
    ratingLine = pick([
      `With a ${rating.toFixed(1)}-star rating from ${reviews} reviews, this is one of the top-rated access points in ${city}. The community clearly loves this spot.`,
      `The ${rating.toFixed(1)}/5 rating backed by ${reviews} reviews speaks for itself — visitors consistently have great experiences here.`,
    ], h + 6);
  } else if (rating && rating >= 3.5) {
    ratingLine = pick([
      `Rated ${rating.toFixed(1)} out of 5 with ${reviews} reviews, it's a solid choice in the area. Most visitors come away satisfied.`,
      `At ${rating.toFixed(1)} stars from ${reviews} reviews, it's a reliable pick for a good beach day in ${city}.`,
    ], h + 6);
  } else if (rating) {
    ratingLine = pick([
      `It carries a ${rating.toFixed(1)}-star rating from ${reviews} reviews. It may not be the flashiest spot, but it has its own quiet appeal.`,
      `With ${reviews} reviews averaging ${rating.toFixed(1)} stars, expectations should be modest — but sometimes the understated spots surprise you.`,
    ], h + 6);
  }

  // Sub-type commentary
  let subTypeLine = "";
  if (subTypes.length > 1) {
    const features = subTypes.filter(s => s !== "Public beach").slice(0, 3);
    if (features.length > 0) {
      subTypeLine = `Beyond basic beach access, this location is also categorized as ${features.join(" and ").toLowerCase()}, which adds to its appeal.`;
    }
  }

  // Closing
  const closers = [
    `Overall, ${name} is a solid public beach access point in ${city}, ${state}. Whether you're a local or just passing through, it's worth a visit.`,
    `Bottom line: if you're in the ${city} area and need a reliable beach access point, ${name} won't disappoint.`,
    `We'd recommend ${name} to anyone exploring ${state}'s public beaches — it's a genuine, no-frills access point that delivers on the essentials.`,
    `All in all, ${name} earns its place on the ${city} beach access map. Pack your sunscreen and enjoy.`,
    `For a straightforward, enjoyable beach outing in ${city}, ${name} checks the right boxes. Just remember to verify current conditions before you go.`,
  ];

  return (
    <div className="mt-12 mb-8" data-testid="our-take-section">
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-[#FEF3C7] rounded-xl flex items-center justify-center">
            <Info className="w-5 h-5 text-[#F59E0B]" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 font-['Outfit']">Our Take</h2>
        </div>
        <div className="prose prose-gray max-w-none text-gray-600 leading-relaxed space-y-4">
          <p>{pick(openers, h)} {pick(areaVibes, h + 10)}</p>
          {amenityLines.length > 0 && (
            <p>{amenityLines.join(" ")}</p>
          )}
          {ratingLine && <p>{ratingLine}</p>}
          {subTypeLine && <p>{subTypeLine}</p>}
          <p>{pick(closers, h + 20)}</p>
        </div>
        <p className="text-xs text-gray-400 mt-5 italic">
          Information is deemed reliable but not guaranteed. Please verify all details before your visit.
        </p>
      </div>
    </div>
  );
};

const BeachDetailPage = () => {
  const { slug } = useParams();
  const [beach, setBeach] = useState(null);
  const [nearbyBeaches, setNearbyBeaches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const beachData = await getBeachBySlug(slug);
        setBeach(beachData);

        try {
          const nearbyData = await getNearbyBeaches(slug, 4);
          setNearbyBeaches(nearbyData.nearby || []);
        } catch (e) {
          console.log("No nearby beaches found");
        }
      } catch (error) {
        console.error("Error fetching beach:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  const handleDirections = () => {
    if (beach?.latitude && beach?.longitude) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${beach.latitude},${beach.longitude}`,
        "_blank"
      );
    } else if (beach?.full_address) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(beach.full_address)}`,
        "_blank"
      );
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: beach?.name,
          text: `Check out ${beach?.name} on PublicBeachAccess.com`,
          url: url,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleSeeReviews = () => {
    if (beach?.google_place_url) {
      window.open(beach.google_place_url, "_blank");
    }
  };

  const amenities = [
    { key: "has_parking", icon: Car, label: "Parking Available", color: "bg-blue-100 text-blue-600" },
    { key: "has_bathrooms", icon: Bath, label: "Restrooms", color: "bg-orange-100 text-orange-600" },
    { key: "has_showers", icon: Waves, label: "Showers", color: "bg-green-100 text-green-600" },
    { key: "is_accessible", icon: Accessibility, label: "Wheelchair Accessible", color: "bg-purple-100 text-purple-600" },
    { key: "is_pet_friendly", icon: PawPrint, label: "Pet Friendly", color: "bg-amber-100 text-amber-600" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="beach-detail-loading">
        <div className="bg-white border-b border-gray-200 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="h-6 w-64 bg-gray-200 animate-pulse rounded mb-4" />
            <div className="h-10 w-96 bg-gray-200 animate-pulse rounded" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 h-[400px] bg-gray-200 animate-pulse rounded-2xl" />
            <div className="lg:col-span-2 h-[300px] bg-gray-200 animate-pulse rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!beach) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🏖️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Beach Not Found</h1>
          <Link to="/search">
            <Button className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              Find Beaches
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const activeAmenities = amenities.filter((a) => beach[a.key] === true);

  // Beach image for the listing
  const BEACH_IMAGE = "/images/hero/beach-default.jpg";

  // Generate a beach image URL based on the state
  const getBeachImage = () => {
    return BEACH_IMAGE;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="beach-detail-page">
      <SeoHead
        title={`${beach.name} - Beach Access in ${beach.city}, ${beach.state}`}
        description={`Visit ${beach.name} in ${beach.city}, ${beach.state}. ${beach.full_address ? `Located at ${beach.full_address.replace(/, United States$/, '')}. ` : ''}${activeAmenities.length > 0 ? `Amenities include ${activeAmenities.map(a => a.label).join(', ')}. ` : ''}${beach.average_rating ? `Rated ${beach.average_rating}/5 from ${beach.total_reviews} reviews. ` : ''}Get directions, hours, and nearby beach access points.`}
        path={`/beach/${beach.slug}`}
        type="place"
      />
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Beach",
        "name": beach.name,
        "description": `${beach.name} is a public beach access point in ${beach.city}, ${beach.state}.`,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": beach.city,
          "addressRegion": beach.state,
          "streetAddress": beach.street || undefined,
          "postalCode": beach.postal_code || undefined,
        },
        ...(beach.latitude && beach.longitude ? { "geo": { "@type": "GeoCoordinates", "latitude": beach.latitude, "longitude": beach.longitude } } : {}),
        ...(beach.average_rating ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": beach.average_rating, "reviewCount": beach.total_reviews || 1 } } : {}),
        "isAccessibleForFree": true,
        "publicAccess": true,
        "url": `https://publicbeachaccess.com/beach/${beach.slug}`,
      }) }} />
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#0EA5E9]">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/search" className="hover:text-[#0EA5E9]">All States</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/state/${beach.state_slug}`} className="hover:text-[#0EA5E9]">{beach.state}</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/state/${beach.state_slug}/${beach.city_slug}`} className="hover:text-[#0EA5E9]">{beach.city}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-800 font-medium truncate max-w-[200px]">{beach.name}</span>
          </nav>

          {/* Title Row */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1
                className="text-2xl sm:text-3xl font-extrabold text-gray-800 font-['Outfit'] tracking-tight"
                data-testid="beach-name"
              >
                {beach.name}
              </h1>
              <div className="flex items-center gap-2 text-gray-500 mt-2">
                <MapPin className="w-4 h-4 text-[#0EA5E9]" />
                <span>{beach.city}, {beach.state}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleShare}
                className="border-2 border-gray-200 hover:bg-gray-50"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                onClick={handleDirections}
                className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold"
                data-testid="get-directions-btn"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Get Directions
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Blue Divider */}
      <div className="h-2 bg-[#0EA5E9]" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Column - Image & Overview */}
          <div className="lg:col-span-3 space-y-6">
            {/* Beach Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img
                src={getBeachImage()}
                alt={beach.name}
                className="w-full h-[350px] sm:h-[450px] object-cover"
              />
              {/* Badge */}
              <div className="absolute top-4 left-4">
                <span className="bg-[#10B981] text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg">
                  🏖️ Public Access
                </span>
              </div>
              {/* Rating Badge */}
              {beach.average_rating && (
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg flex items-center gap-1">
                  <Star className="w-5 h-5 fill-[#FBBF24] text-[#FBBF24]" />
                  <span className="font-bold text-gray-800">{beach.average_rating.toFixed(1)}</span>
                  {beach.total_reviews && (
                    <span className="text-gray-500 text-sm">({beach.total_reviews})</span>
                  )}
                </div>
              )}
            </div>

            {/* Overview Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Overview</h2>
              <p className="text-gray-600 leading-relaxed">
                {beach.name} is a public beach access point located in {beach.city}, {beach.state}. 
                {beach.full_address && <> The beach is situated at <strong>{beach.full_address.replace(/, United States$/, '')}</strong>.</>}
                {activeAmenities.length > 0 && ` This location offers ${activeAmenities.map(a => a.label.toLowerCase()).join(", ")}.`}
                {" "}Visit this beach access for a great day at the shore!
              </p>
            </div>

            {/* Amenities Card */}
            {activeAmenities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Amenities</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {activeAmenities.map((amenity) => (
                    <div
                      key={amenity.key}
                      className={`flex items-center gap-3 p-4 rounded-xl ${amenity.color}`}
                    >
                      <amenity.icon className="w-6 h-6" />
                      <span className="font-medium text-sm">{amenity.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Info */}
            {(beach.business_phone || beach.business_website) && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Contact</h2>
                <div className="space-y-3">
                  {beach.business_phone && (
                    <a
                      href={`tel:${beach.business_phone}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-[#0EA5E9] transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                      <span>{beach.business_phone}</span>
                    </a>
                  )}
                  {beach.business_website && (
                    <a
                      href={beach.business_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-600 hover:text-[#0EA5E9] transition-colors"
                    >
                      <Globe className="w-5 h-5" />
                      <span className="truncate">{beach.business_website}</span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Info & Map */}
          <div className="lg:col-span-2 space-y-6">
            {/* Information Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Information</h2>
              
              {/* Address */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#DBEAFE] rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-bold text-gray-800">
                    {(beach.full_address || `${beach.city}, ${beach.state}`).replace(/, United States$/, '')}
                  </p>
                  {beach.postal_code && (
                    <p className="text-gray-500 text-sm">{beach.postal_code}</p>
                  )}
                </div>
              </div>

              <Separator className="my-4" />

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleDirections}
                  className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold h-12"
                  data-testid="directions-card-btn"
                >
                  <Navigation className="w-5 h-5 mr-2" />
                  Get Directions
                </Button>
                
                {beach.google_place_url && (
                  <Button
                    variant="outline"
                    onClick={handleSeeReviews}
                    className="w-full border-2 border-gray-200 hover:bg-gray-50 h-12"
                    data-testid="reviews-btn"
                  >
                    <Star className="w-5 h-5 mr-2" />
                    See Reviews
                  </Button>
                )}
              </div>
            </div>

            {/* Location Map Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Location</h2>
              <div className="h-[300px] rounded-xl overflow-hidden">
                {beach.latitude && beach.longitude ? (
                  <BeachMap
                    beaches={[beach]}
                    center={[beach.latitude, beach.longitude]}
                    zoom={14}
                    fitBounds={false}
                  />
                ) : (
                  <div className="h-full bg-gray-100 flex items-center justify-center text-gray-500">
                    Map not available
                  </div>
                )}
              </div>
              {beach.latitude && beach.longitude && (
                <p className="text-xs text-gray-400 mt-2 font-mono">
                  {beach.latitude.toFixed(6)}, {beach.longitude.toFixed(6)}
                </p>
              )}
            </div>

            {/* Categories */}
            {beach.sub_types && beach.sub_types.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Categories</h2>
                <div className="flex flex-wrap gap-2">
                  {beach.sub_types.map((type, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-600 font-medium"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nearby Beaches */}
        {nearbyBeaches.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800 font-['Outfit']">
                More Beach Access in {beach.city}
              </h2>
              <Link
                to={`/state/${beach.state_slug}/${beach.city_slug}`}
                className="text-[#0EA5E9] font-semibold hover:underline flex items-center"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="nearby-beaches">
              {nearbyBeaches.map((nearby) => (
                <BeachCard key={nearby.id} beach={nearby} compact />
              ))}
            </div>
          </div>
        )}

        {/* Our Take */}
        <OurTake beach={beach} activeAmenities={activeAmenities} />
      </div>

      <Footer />
    </div>
  );
};

export default BeachDetailPage;
